import { FC } from 'react';

import { isAccountStatusReadOnly } from 'utils/clickme';
import { CREATE_MENU_PAGE_ROUTE, MULTI_CREATION_ROUTE } from 'utils/constants/app';
import { Settings } from 'utils/constants/dynamicSettings';

import multicreationOpenButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/auction/multicreation/multicreation_open_button_click';
import campaignCreateButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/common/campaign_create_button_click';
import { HSpacing, useBreakpoint, VSpacing } from '@hh.ru/magritte-ui';
import { Link } from '@hh.ru/redux-spa-middleware';

import Button, { ButtonKind } from 'bloko/blocks/button';
import DropInfo, { InfoPlacement, InfoTheme } from 'bloko/blocks/drop/Info';
import localStorageWrapper from 'bloko/common/storage/LocalStorageWrapper';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';
import useSettings from 'hooks/useSettings';

import withErrorBoundary from 'components/common/ErrorBoundary/withErrorBoundary';
import CampaignsSearch from 'components/Dashboard/CampaignsSearch/CampaignsSearch';
import { DownloadStatisticButton } from 'components/Dashboard/DownloadStatisticButton/DownloadStatisticButton';
import Filters, { IS_CAMPAIGNS_FILTERS_NEW_TIP_WAS_SHOWN } from 'components/Dashboard/Filters/Filters';
import LeadsButton from 'components/Dashboard/LeadsButton/LeadsButton';

import { useSelector } from 'store/utils';

import styles from 'components/Dashboard/DashboardButtons/DashboardButtons.module.less';

export const IS_MULTI_CREATION_TIP_WAS_SHOWN = 'IS_MULTI_CREATION_TIP_WAS_SHOWN';

const Trls = {
    newCampaignButton: 'dashboard.title.newCampaign.button',
    newCampaignButtonMobile: 'dashboard.title.newCampaign.button.isMobile',
    multiCreationDropInfo: 'Dashboard.DashboardButtons.multiCreation.DropInfo',
    multiCreationButton: 'Dashboard.DashboardButtons.multiCreation.button',
};

const DashboardButtons: FC = () => {
    const currentAccountStatus = useSelector(({ user }) => user.user?.currentAccount.status);
    const [isMultiCreationTipIsVisible, , setIsMultiCreationTipIsVisibleOff] = useOnOffState(
        !localStorageWrapper.getItem(IS_MULTI_CREATION_TIP_WAS_SHOWN)
    );

    const trl = useTrls();
    const settings = useSettings();
    const isLeadsShow = settings(Settings.VkadsLeadFormAccountIds) as boolean;

    const isCampaignsFiltersNewTipWasShown = Boolean(
        localStorageWrapper.getItem(IS_CAMPAIGNS_FILTERS_NEW_TIP_WAS_SHOWN)
    );

    const { isXS, isGtS } = useBreakpoint();

    return (
        <>
            <div className={styles.wrapper}>
                {!isAccountStatusReadOnly(currentAccountStatus) && (
                    <>
                        <div className={styles.buttonNewWrapper}>
                            <Button
                                onClick={() => campaignCreateButtonClick()}
                                kind={ButtonKind.Primary}
                                data-qa="new-campaign"
                                Element={Link}
                                to={CREATE_MENU_PAGE_ROUTE}
                                stretched={isXS}
                            >
                                {isXS ? trl(Trls.newCampaignButtonMobile) : trl(Trls.newCampaignButton)}
                            </Button>
                        </div>
                        <DropInfo
                            show={isMultiCreationTipIsVisible && (isCampaignsFiltersNewTipWasShown || isGtS)}
                            placement={InfoPlacement.BottomStart}
                            showCloseButton
                            theme={InfoTheme.Dark}
                            host={document.body}
                            closeByClickOutside={false}
                            onClose={() => {
                                setIsMultiCreationTipIsVisibleOff();
                                localStorageWrapper.setItem(IS_MULTI_CREATION_TIP_WAS_SHOWN, 'true');
                            }}
                            render={() => trl(Trls.multiCreationDropInfo)}
                        >
                            <Button
                                data-qa="multicreation-button"
                                Element={Link}
                                to={MULTI_CREATION_ROUTE}
                                onClick={() => multicreationOpenButtonClick()}
                            >
                                {trl(Trls.multiCreationButton)}
                            </Button>
                        </DropInfo>
                    </>
                )}
                {!isXS && <CampaignsSearch />}
                <Filters />
                <div className={styles.buttonsRight}>
                    {isLeadsShow && (
                        <>
                            <LeadsButton />
                            <HSpacing default={8} />
                        </>
                    )}
                    <DownloadStatisticButton />
                </div>
            </div>
            {isXS && (
                <>
                    <VSpacing default={20} />
                    <CampaignsSearch />
                </>
            )}
            <VSpacing default={24} />
        </>
    );
};

export default withErrorBoundary(DashboardButtons);
